import {
  completes,
  createDateRange,
  getFormattedTimeStringFromPeriod,
  getPriorityCell,
  noBarData,
  noData,
  starts,
  validLineData,
  type PerformanceFilter,
} from "../../../../Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";

import { useQuery } from "@tanstack/react-query";
import {
  getEmailEngagementCards,
  getEmailEngagementChart,
  getFlowEngagementChart,
  getLineChartQuery,
  getPrioritizedFlowCards,
  getPrioritizedFlowLineChart,
  fetchPrioritizedFlowTable,
  getSummaryCards,
  getUserActivityEngagement,
  getUserEnrollmentCards,
  getUserEnrollmentLineChart,
} from "../Queries/queries";

import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";

import styles from "./groupPerformanceBody.module.scss";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { ChartWrapper, getDateFormatByCount, GroupedBarChart, HorizontalBarChart, LineChart } from "components/charts";
import { useCallback, useMemo, useState } from "react";
import { useGoogleTooltipFormats } from "features/Reporting/Google/utils/useGoogleTooltipFormats";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import { ContentView, TextTruncate } from "components";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type PrioritizedFlowTable } from "./types";
import { type ColumnOption } from "interfaces/columnOptions";
import { Tooltip } from "components/common/tooltip";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { type PaginationParams } from "features/Library/Common/models";

const line = "line";
const column = "column";

export type ChartState = typeof line | typeof column;

export type Props = PropsFromRedux & {
  filter: PerformanceFilter;
  groupId: number;
  dateRange: Date[];
};

type FlowColumnOption = ColumnOption<PrioritizedFlowTable> & { sortKey?: string };

const columnOptions: FlowColumnOption[] = [
  {
    name: "FLOW NAME",
    width: 5,
    isSortable: true,
    sortKey: "FlowTitle",
    render: (item: PrioritizedFlowTable) => {
      return (
        <Tooltip
          target={
            <TextTruncate>
              <MoboLink to={`/${RouteNames.contentFlows}/${item.FlowId}`}>{item.FlowTitle}</MoboLink>
            </TextTruncate>
          }
          content={item.FlowTitle}
        />
      );
    },
  },
  {
    name: "PRIORITY LEVEL",
    width: 3,
    isSortable: true,
    sortKey: "Priority",
    render: (item: PrioritizedFlowTable) => {
      return getPriorityCell(item.Priority);
    },
  },
  {
    name: "USERS NOT STARTED",
    width: 3,
    isSortable: true,
    sortKey: "NotStarted",
    render: (item: PrioritizedFlowTable) => item.NotStarted,
  },
  {
    name: "USERS STARTED",
    width: 3,
    isSortable: true,
    sortKey: "Started",
    render: (item: PrioritizedFlowTable) => item.Started,
  },
  {
    name: "USERS COMPLETED",
    width: 3,
    isSortable: true,
    sortKey: "Completed",
    render: (item: PrioritizedFlowTable) => item.Completed,
  },
];

const prioritizedFlowChartTitles = ["Prioritized User Activity - Line Chart", "Prioritized User Activity - Bar Chart"];

const prioritizedFlowLegendLabels = ["Incomplete", "Not Started", "Started", "Completed"];

const prioritizedFlowColorRange = [
  reportingColors.greyLight,
  reportingColors.greyLighter,
  reportingColors.yellow,
  reportingColors.greenLight,
];

const handleDropdownChange = (dropdownValue: string, setFunc: (newState: ChartState) => void) => {
  dropdownValue.indexOf("Bar") === -1 ? setFunc(line) : setFunc(column);
};

export const GroupPerformanceBody: React.FC<Props> = ({ filter, groupId, setHasExportData }) => {
  const isReportEnabled = !!useFeatureFlag(FeatureFlags.GroupPerformance);
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);
  const [activityChartState, setActivityChartState] = useState<ChartState>(line);
  const [prioritizedFlowChartState, setPrioritizedFlowChartState] = useState<ChartState>(line);
  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const summaryCardsQuery = useQuery({
    queryKey: ["groupSummaryCards", filter, groupId],
    queryFn: getSummaryCards,
    enabled: isReportEnabled,
  });

  const { groupedBarFormatter } = useGoogleTooltipFormats(chartPeriod, dateRange);

  const userActivityLineChartQuery = useQuery({
    queryKey: ["userCollaborationLine", filter, groupId, chartPeriod],
    queryFn: getLineChartQuery,
    enabled: isReportEnabled && chartPeriod !== CHART_PERIOD.UNSET,
  });

  const userActivityEngagementQuery = useQuery({
    queryKey: ["userActivityEngagement", filter, groupId],
    queryFn: getUserActivityEngagement,
    enabled: isReportEnabled,
  });

  const userEnrollmentLineChartQuery = useQuery({
    queryKey: ["userEnrollmentLine", filter, groupId, chartPeriod],
    queryFn: getUserEnrollmentLineChart,
    enabled: isReportEnabled && chartPeriod !== CHART_PERIOD.UNSET,
  });

  const userEnrollmentCardsQuery = useQuery({
    queryKey: ["userEnrollmentCards", filter, groupId],
    queryFn: getUserEnrollmentCards,
    enabled: isReportEnabled,
  });

  const flowEngagementChartQuery = useQuery({
    queryKey: ["flowEngagementChart", filter, groupId],
    queryFn: getFlowEngagementChart,
    enabled: isReportEnabled,
  });

  const emailEngagementChartQuery = useQuery({
    queryKey: ["emailEngagementChart", filter, groupId],
    queryFn: getEmailEngagementChart,
    enabled: isReportEnabled,
  });

  const emailEngagementCardsQuery = useQuery({
    queryKey: ["emailEngagementCards", filter, groupId],
    queryFn: getEmailEngagementCards,
    enabled: isReportEnabled,
  });

  const prioritizedFlowCardsQuery = useQuery({
    queryKey: ["prioritizedFlowCards", filter, groupId],
    queryFn: getPrioritizedFlowCards,
    enabled: isReportEnabled,
  });

  const prioritizedFlowLineChartQuery = useQuery({
    queryKey: ["prioritizedFlowLineChart", filter, groupId, chartPeriod],
    queryFn: getPrioritizedFlowLineChart,
    enabled: isReportEnabled && chartPeriod !== CHART_PERIOD.UNSET,
  });

  const prioritizedFlowTableQueryKey = useMemo(
    () => ["prioritizedFlowTable", filter, groupId] as const,
    [filter, groupId],
  );

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This group has no user activity.",
      }),
    [setHasExportData],
  );

  const fetchPrioritizedFlowData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) => fetchPrioritizedFlowTable(filter, groupId, params, signal),
    [filter, groupId],
  );

  const activeUsersChart = useMemo(() => {
    if (!userActivityLineChartQuery.data) {
      return <></>;
    }
    return activityChartState === line ? (
      <LineChart
        {...sharedAccountReportingLineProps}
        xData={[userActivityLineChartQuery.data?.lineData.Date, userActivityLineChartQuery.data?.lineData.Date]}
        yData={[
          userActivityLineChartQuery.data?.lineData.TotalOutreach,
          userActivityLineChartQuery.data?.lineData.TotalInteractions,
        ]}
        xFormatterFunc={(d) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
        colorRange={[reportingColors.greenDark, reportingColors.blue]}
      />
    ) : (
      <GroupedBarChart
        {...sharedGoogleReportingBarProps}
        data={userActivityLineChartQuery.data.activityBarData}
        order={userActivityLineChartQuery.data.order}
        xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toLocaleString(), chartPeriod, dateRange)}
        tooltipFormatter={groupedBarFormatter}
        colorRange={[reportingColors.greenDark, reportingColors.blue]}
      />
    );
  }, [userActivityLineChartQuery.data, activityChartState, chartPeriod, dateRange, groupedBarFormatter]);

  const prioritizedFlowChart = useMemo(() => {
    if (!prioritizedFlowLineChartQuery.data) {
      return null;
    }

    const { lineData, activityBarData, order } = prioritizedFlowLineChartQuery.data;

    if (prioritizedFlowChartState === line) {
      return (
        <LineChart
          {...sharedAccountReportingLineProps}
          xData={[lineData.Date, lineData.Date, lineData.Date, lineData.Date]}
          yData={[lineData.Incomplete, lineData.NotStarted, lineData.Started, lineData.Completed]}
          xFormatterFunc={getDateFormatByCount(lineData?.Date?.length)}
          colorRange={prioritizedFlowColorRange}
          tooltipFormatter={(_x, y) => roundToTwoDigits(y)}
        />
      );
    }

    return (
      <GroupedBarChart
        {...sharedGoogleReportingBarProps}
        data={activityBarData}
        order={order}
        xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toLocaleString(), chartPeriod, dateRange)}
        tooltipFormatter={groupedBarFormatter}
        colorRange={prioritizedFlowColorRange}
      />
    );
  }, [prioritizedFlowLineChartQuery.data, prioritizedFlowChartState, chartPeriod, dateRange, groupedBarFormatter]);

  const contentViewTabs = [
    {
      id: "prioritizedFlows",
      label: "Prioritized Flows",
      component: (
        <div className={styles.contentReport}>
          <div className={styles.charts}>
            <ChartWrapper
              titles={prioritizedFlowChartTitles}
              showLegend
              legendLabels={prioritizedFlowLegendLabels}
              colorRange={prioritizedFlowColorRange}
              onChange={(option) => handleDropdownChange(option, setPrioritizedFlowChartState)}
            >
              <RequestStatusRenderer state={prioritizedFlowLineChartQuery.status}>
                {prioritizedFlowLineChartQuery.isSuccess &&
                validLineData([
                  prioritizedFlowLineChartQuery.data.lineData.Incomplete,
                  prioritizedFlowLineChartQuery.data.lineData.NotStarted,
                  prioritizedFlowLineChartQuery.data.lineData.Started,
                  prioritizedFlowLineChartQuery.data.lineData.Completed,
                ])
                  ? prioritizedFlowChart
                  : noData(filter)}
              </RequestStatusRenderer>
            </ChartWrapper>
            <ChartWrapper titles={["Prioritized Users by Status"]}>
              <RequestStatusRenderer state={prioritizedFlowCardsQuery.status}>
                {prioritizedFlowCardsQuery.isSuccess &&
                !noBarData(...prioritizedFlowCardsQuery.data.barData.map((data) => data.value)) ? (
                  <HorizontalBarChart
                    {...sharedAccountReportingHorizontalBarProps}
                    data={prioritizedFlowCardsQuery.data.barData}
                  />
                ) : (
                  noData(filter)
                )}
              </RequestStatusRenderer>
            </ChartWrapper>
          </div>
          <div className={styles.cards}>
            <RequestStatusRenderer state={prioritizedFlowCardsQuery.status}>
              {prioritizedFlowCardsQuery.isSuccess && <CardReporting items={prioritizedFlowCardsQuery.data.cards} />}
            </RequestStatusRenderer>
          </div>
          <div className={styles.prioritizedFlows}>
            <GenericPerformanceListWithServerPagination
              title="Prioritized Flows"
              columns={columnOptions}
              filter={filter}
              fetchPage={fetchPrioritizedFlowData}
              queryKeyBase={prioritizedFlowTableQueryKey}
              hasDataForExport={updateExportDataState}
            />
          </div>
        </div>
      ),
    },
    {
      id: "flowEngagement",
      label: "Flow Engagement",
      component: (
        <div className={styles.contentReport}>
          <div className={styles.activity}>
            <ChartWrapper
              titles={["Flow Engagement"]}
              legendLabels={[starts, completes]}
              showLegend
              colorRange={[reportingColors.blue, reportingColors.yellow]}
            >
              <RequestStatusRenderer state={flowEngagementChartQuery.status}>
                {flowEngagementChartQuery.isSuccess &&
                !noBarData(...flowEngagementChartQuery.data.barData.map((d) => d.value)) ? (
                  <HorizontalBarChart
                    {...sharedAccountReportingHorizontalBarProps}
                    data={flowEngagementChartQuery.data.barData}
                  />
                ) : (
                  noData(filter)
                )}
              </RequestStatusRenderer>
            </ChartWrapper>
          </div>
          <div className={styles.cards}>
            <RequestStatusRenderer state={flowEngagementChartQuery.status}>
              {flowEngagementChartQuery.isSuccess && (
                <CardReporting items={flowEngagementChartQuery.data.cards} columnCount={3} />
              )}
            </RequestStatusRenderer>
          </div>
        </div>
      ),
    },
    {
      id: "emailEngagement",
      label: "Email Engagement",
      component: (
        <div className={styles.contentReport}>
          <div className={styles.activity}>
            <ChartWrapper
              titles={["Email Engagement"]}
              legendLabels={["Sends", "Opens", "Clicks"]}
              showLegend
              colorRange={[reportingColors.blue, reportingColors.greenDark, reportingColors.yellow]}
            >
              <RequestStatusRenderer state={emailEngagementChartQuery.status}>
                {emailEngagementChartQuery.isSuccess &&
                !noBarData(...emailEngagementChartQuery.data.map((d) => d.value)) ? (
                  <HorizontalBarChart
                    {...sharedAccountReportingHorizontalBarProps}
                    data={emailEngagementChartQuery.data}
                  />
                ) : (
                  noData(filter)
                )}
              </RequestStatusRenderer>
            </ChartWrapper>
          </div>
          <div className={styles.cards}>
            <RequestStatusRenderer state={emailEngagementCardsQuery.status}>
              {emailEngagementCardsQuery.isSuccess && <CardReporting items={emailEngagementCardsQuery.data} />}
            </RequestStatusRenderer>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.cards}>
        <RequestStatusRenderer state={summaryCardsQuery.status}>
          {summaryCardsQuery.isSuccess && <CardReporting items={summaryCardsQuery.data} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.header}>
        <h2>Users</h2>
      </div>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["Activity - Line Chart", "Activity - Bar Chart"]}
          legendLabels={["Platform Outreach", "Platform Interactions"]}
          showLegend
          onChange={(option) => handleDropdownChange(option, setActivityChartState)}
          colorRange={[reportingColors.greenDark, reportingColors.blue]}
        >
          <RequestStatusRenderer state={userActivityLineChartQuery.status}>
            {userActivityLineChartQuery.isSuccess &&
            validLineData([
              userActivityLineChartQuery.data.lineData.TotalInteractions,
              userActivityLineChartQuery.data.lineData.TotalOutreach,
            ])
              ? activeUsersChart
              : noData(filter)}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={userActivityEngagementQuery.status}>
          {userActivityEngagementQuery.isSuccess && <CardReporting items={userActivityEngagementQuery.data} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.activity}>
        <ChartWrapper
          titles={["User Enrollment Activity"]}
          legendLabels={["Total Enrolled", "Added", "Dropped"]}
          showLegend
          colorRange={[reportingColors.blue, reportingColors.greenDark, reportingColors.red]}
        >
          <RequestStatusRenderer state={userEnrollmentLineChartQuery.status}>
            {userEnrollmentLineChartQuery.isSuccess &&
            validLineData([
              userEnrollmentLineChartQuery.data.totalJoined,
              userEnrollmentLineChartQuery.data.totalMembers,
              userEnrollmentLineChartQuery.data.totalLeft,
            ]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xData={[
                  userEnrollmentLineChartQuery.data?.Date,
                  userEnrollmentLineChartQuery.data?.Date,
                  userEnrollmentLineChartQuery.data?.Date,
                ]}
                yData={[
                  userEnrollmentLineChartQuery.data?.totalMembers,
                  userEnrollmentLineChartQuery.data?.totalJoined,
                  userEnrollmentLineChartQuery.data?.totalLeft,
                ]}
                xFormatterFunc={(d) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
                colorRange={[reportingColors.blue, reportingColors.greenDark, reportingColors.red]}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={userEnrollmentCardsQuery.status}>
          {userEnrollmentCardsQuery.isSuccess && <CardReporting items={userEnrollmentCardsQuery.data} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.header}>
        <h2>Content</h2>
      </div>
      <div className={styles.bottomReport}>
        <ContentView tabs={contentViewTabs} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(GroupPerformanceBody);
export default ConnectedComponent;
